import { notify } from '@/lib/toast';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import { Layers, Plus, Edit2, Trash2, Package, X, Search, Percent, Users, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { PageHeader } from '@/components/ui/page-header';
import { StatusBadge } from '@/components/ui/status-badge';
import { FormDialog } from '@/components/ui/form-dialog';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { useTranslation } from 'react-i18next';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Dialog } from 'primereact/dialog';
import { formatCurrency } from '@/lib/utils';

const tierSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  adjustmentPercent: z.coerce.number().min(-100, 'Cannot go below -100%').max(500, 'Cannot exceed 500%'),
});

type TierForm = z.infer<typeof tierSchema>;

interface PriceTier {
  id: string;
  name: string;
  description?: string | null;
  adjustmentPercent: number | string;
  isActive: boolean;
  _count?: { customers?: number; productPrices?: number };
}

interface PriceTierProductPrice {
  id: string;
  productId: string;
  adjustmentPercent: number | string;
  product?: { name?: string; salePrice?: number | string };
}

interface PriceTierDetail extends PriceTier {
  productPrices?: PriceTierProductPrice[];
}

interface PriceTierSearchProduct {
  id: string;
  name: string;
  salePrice: number | string;
}

interface ProductSearchResult {
  data?: PriceTierSearchProduct[];
}

export function PriceTiersPage() {
  const { t } = useTranslation('products');
  const { hasPermission } = useAuthStore();
  const canRead = hasPermission('price-tiers:read') || hasPermission('price-tiers:*') || hasPermission('products:*') || hasPermission('products:read');
  const canWrite = hasPermission('price-tiers:write') || hasPermission('price-tiers:*') || hasPermission('products:write') || hasPermission('products:*');

  // Create/Edit modal state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTier, setEditTier] = useState<PriceTier | null>(null);

  // Manage overrides modal state
  const [manageTierId, setManageTierId] = useState<string | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [productAdjPct, setProductAdjPct] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<PriceTierSearchProduct | null>(null);
  const [showOverrides, setShowOverrides] = useState(false);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [removeProductConfirm, setRemoveProductConfirm] = useState<{ productId: string; productName: string } | null>(null);

  // Preview: enter a sample price to see effective tier price
  const [previewBasePrice, setPreviewBasePrice] = useState('100');

  const qc = useQueryClient();

  const { register, handleSubmit, reset, watch, control, formState: { errors } } = useForm<TierForm>({
    resolver: zodResolver(tierSchema),
    defaultValues: { adjustmentPercent: 0 },
  });

  const watchedAdjustment = watch('adjustmentPercent');

  useEffect(() => {
    if (dialogOpen && editTier) {
      reset({
        name: editTier.name,
        description: editTier.description ?? '',
        adjustmentPercent: Number(editTier.adjustmentPercent ?? 0),
      });
    } else if (dialogOpen && !editTier) {
      reset({ name: '', description: '', adjustmentPercent: 0 });
    }
  }, [dialogOpen, editTier, reset]);

  const { data: tiers, isLoading, isError } = useQuery<PriceTier[]>({
    queryKey: ['price-tiers'],
    queryFn: () => api.get('/price-tiers').then((r) => r.data),
    enabled: canRead,
  });

  const { data: tierDetail, isLoading: tierDetailLoading } = useQuery<PriceTierDetail>({
    queryKey: ['price-tier-detail', manageTierId],
    queryFn: () => api.get(`/price-tiers/${manageTierId}`).then((r) => r.data),
    enabled: !!manageTierId,
  });

  const { data: productSearchResult } = useQuery<ProductSearchResult>({
    queryKey: ['products-search', productSearch],
    queryFn: () => api.get(`/products?search=${encodeURIComponent(productSearch)}&limit=10`).then((r) => r.data),
    enabled: !!manageTierId && productSearch.length >= 2,
  });

  const createMutation = useMutation({
    mutationFn: (data: TierForm) => api.post('/price-tiers', data),
    onSuccess: () => {
      toast.success('Price tier created');
      qc.invalidateQueries({ queryKey: ['price-tiers'] });
      setDialogOpen(false);
    },
    onError: (err) => notify.error(err, 'Failed to create tier'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: TierForm & { id: string }) => api.patch(`/price-tiers/${id}`, data),
    onSuccess: () => {
      toast.success('Price tier updated');
      qc.invalidateQueries({ queryKey: ['price-tiers'] });
      setDialogOpen(false);
    },
    onError: (err) => notify.error(err, 'Failed to update tier'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/price-tiers/${id}`),
    onSuccess: () => {
      toast.success('Price tier deleted');
      qc.invalidateQueries({ queryKey: ['price-tiers'] });
      setDeleteConfirmId(null);
    },
    onError: (err) => notify.error(err, 'Failed to delete tier'),
  });

  const setProductPriceMutation = useMutation({
    mutationFn: ({ tierId, productId, adjustmentPercent }: { tierId: string; productId: string; adjustmentPercent: number }) =>
      api.post(`/price-tiers/${tierId}/products/${productId}`, { adjustmentPercent }),
    onSuccess: () => {
      toast.success('Override % set');
      qc.invalidateQueries({ queryKey: ['price-tier-detail', manageTierId] });
      qc.invalidateQueries({ queryKey: ['price-tiers'] });
      setSelectedProduct(null);
      setProductAdjPct('');
      setProductSearch('');
    },
    onError: (err) => notify.error(err, 'Failed to set override'),
  });

  const removeProductMutation = useMutation({
    mutationFn: ({ tierId, productId }: { tierId: string; productId: string }) =>
      api.delete(`/price-tiers/${tierId}/products/${productId}`),
    onSuccess: () => {
      toast.success('Override removed');
      qc.invalidateQueries({ queryKey: ['price-tier-detail', manageTierId] });
      qc.invalidateQueries({ queryKey: ['price-tiers'] });
      setRemoveProductConfirm(null);
    },
    onError: (err) => notify.error(err, 'Failed to remove override'),
  });

  const onSubmit = (data: TierForm) => {
    if (editTier) updateMutation.mutate({ id: editTier.id, ...data });
    else createMutation.mutate(data);
  };

  const openCreate = () => { setEditTier(null); setDialogOpen(true); };
  const openEdit = (tier: PriceTier) => { setEditTier(tier); setDialogOpen(true); };

  const handleAddOverride = () => {
    if (!selectedProduct || !productAdjPct || !manageTierId) return;
    const pct = parseFloat(productAdjPct);
    if (isNaN(pct) || pct < -100 || pct > 500) { toast.error('Override % must be between -100 and 500'); return; }
    setProductPriceMutation.mutate({ tierId: manageTierId, productId: selectedProduct.id, adjustmentPercent: pct });
  };

  const addedProductIds = new Set<string>((tierDetail?.productPrices ?? []).map((p: PriceTierProductPrice) => p.productId));
  const searchableProducts = (productSearchResult?.data ?? []).filter((p: PriceTierSearchProduct) => !addedProductIds.has(p.id));

  // Live preview calculation
  const basePriceNum = parseFloat(previewBasePrice) || 0;
  const adjustNum = typeof watchedAdjustment === 'number' ? watchedAdjustment : parseFloat(String(watchedAdjustment)) || 0;
  const previewEffective = basePriceNum > 0 ? basePriceNum * (1 + adjustNum / 100) : 0;

  if (!canRead) {
    return <div className="p-8 text-center text-muted-foreground">You don't have permission to view price tiers.</div>;
  }

  const managingTier = tiers?.find((pt: PriceTier) => pt.id === manageTierId);

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Price Tiers"
        subtitle="Assign a discount % to a tier — customers in that tier automatically get discounted prices on every sale"
        actions={canWrite ? (
          <Button onClick={openCreate}>
            <Plus className="me-2 h-4 w-4" />
            Add Tier
          </Button>
        ) : undefined}
      />

      {/* How it works banner */}
      <div className="flex items-start gap-3 rounded-lg border border-info/30 bg-info/10 px-4 py-3 text-sm text-info-text">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <span className="font-semibold">How price tiers work: </span>
        Set a price adjustment % on each tier. Use a <span className="font-semibold">negative number to discount</span> (e.g. -25 = 25% off) or a <span className="font-semibold">positive number to mark up</span> (e.g. +10 = 10% surcharge).
          Any customer assigned to that tier is automatically charged the adjusted price on every product.
          If a specific product needs a <span className="font-semibold">different %</span>, add a per-product % override.
          Bulk price changes automatically flow through — no manual updates needed.
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isError && (
            <div className="rounded-t-xl border-b border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
              Failed to load price tiers. Please refresh.
            </div>
          )}
          {isLoading ? (
            <SkeletonTable rows={5} cols={canWrite ? 6 : 5} />
          ) : !tiers?.length ? (
            <EmptyState
              icon={Layers}
              title="No price tiers yet"
              subtitle={canWrite ? 'Create a tier to offer discounts to specific customer groups' : undefined}
              action={canWrite ? { label: 'Add Tier', onClick: openCreate } : undefined}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('tbl.tierName')}</Th>
                    <Th>{t('tbl.discount')}</Th>
                    <Th>{t('description')}</Th>
                    <Th>{t('tbl.customers')}</Th>
                    <Th>{t('tbl.overrides')}</Th>
                    <Th>{t('tbl.status')}</Th>
                    {canWrite && <Th className="text-end">{t('actionsColumn')}</Th>}
                  </Tr>
                </THead>
                <TBody>
                  {tiers.map((tier: PriceTier) => {
                    const pct = Number(tier.adjustmentPercent ?? 0);
                    return (
                      <Tr key={tier.id}>
                        <Td label={t('tbl.tierName')} className="font-medium text-foreground">{tier.name}</Td>
                        <Td label={t('tbl.discount')}>
                          {pct > 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-semibold text-destructive-text">
                              <Percent className="h-3 w-3" />
                              +{pct}% markup
                            </span>
                          ) : pct < 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-0.5 text-xs font-semibold text-success-text">
                              <Percent className="h-3 w-3" />
                              {pct}% off
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">No adjustment</span>
                          )}
                        </Td>
                        <Td label={t('description')} className="text-muted-foreground">{tier.description ?? '—'}</Td>
                        <Td label={t('tbl.customers')}>
                          <span className="inline-flex items-center gap-1 text-foreground">
                            <Users className="h-3.5 w-3.5 text-muted-foreground" />
                            {tier._count?.customers ?? 0}
                          </span>
                        </Td>
                        <Td label={t('tbl.overrides')} className="text-foreground">{tier._count?.productPrices ?? 0}</Td>
                        <Td label={t('tbl.status')}>
                          <StatusBadge status={tier.isActive ? 'ACTIVE' : 'INACTIVE'} />
                        </Td>
                        {canWrite && (
                          <Td label={t('actionsColumn')}>
                            <TableRowActions
                              actions={[
                                {
                                  key: 'overrides',
                                  label: 'Manage overrides',
                                  icon: Package,
                                  onSelect: () => {
                                    setManageTierId(tier.id);
                                    setProductSearch('');
                                    setSelectedProduct(null);
                                    setProductAdjPct('');
                                    setShowOverrides(false);
                                  },
                                  primary: true,
                                },
                                {
                                  key: 'edit',
                                  label: 'Edit tier',
                                  icon: Edit2,
                                  onSelect: () => openEdit(tier),
                                  primary: true,
                                },
                                {
                                  key: 'delete',
                                  label: 'Delete tier',
                                  icon: Trash2,
                                  onSelect: () => setDeleteConfirmId(tier.id),
                                  variant: 'destructive',
                                },
                              ]}
                            />
                          </Td>
                        )}
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <FormDialog
        open={dialogOpen}
        title={editTier ? 'Edit Price Tier' : 'New Price Tier'}
        width="36rem"
        submitLabel={editTier ? 'Update Tier' : 'Create Tier'}
        isPending={createMutation.isPending || updateMutation.isPending}
        onSubmit={handleSubmit(onSubmit)}
        onClose={() => setDialogOpen(false)}
      >
          <div className="space-y-2">
            <Label htmlFor="tier-name">Tier Name *</Label>
            <Input id="tier-name" {...register('name')} placeholder="e.g. Loyal, Wholesale, VIP, Credit" />
            {errors.name && <p className="text-xs text-destructive-text">{errors.name.message}</p>}
          </div>

            <div className="space-y-2">
              <Label htmlFor="tier-discount">Price Adjustment % *</Label>
              <div className="relative">
                <Controller
                  name="adjustmentPercent"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="tier-discount"
                      type="number"
                      min="-100"
                      max="500"
                      step="0.5"
                      {...field}
                      className="pe-8"
                      placeholder="e.g. -25 for 25% off, +10 for 10% markup"
                    />
                  )}
                />
                <Percent className="absolute end-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
              </div>
              {errors.adjustmentPercent && <p className="text-xs text-destructive-text">{errors.adjustmentPercent.message}</p>}
              <p className="text-xs text-muted-foreground">
                {adjustNum < 0
                  ? <>Customers pay <span className="font-semibold text-success-text">{Math.abs(adjustNum)}% less</span> than base price.</>
                  : adjustNum > 0
                  ? <>Customers pay <span className="font-semibold text-destructive-text">{adjustNum}% more</span> than base price.</>
                  : <>Enter a negative number to discount (e.g. -25), or positive to mark up (e.g. +10).</>}
              </p>
            </div>

            {/* Live preview */}
            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Live Preview</p>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label className="text-xs">Sample Base Price</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={previewBasePrice}
                    onChange={(e) => setPreviewBasePrice(e.target.value)}
                    className="mt-1 h-8 text-sm"
                    placeholder="100"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">Effective Tier Price</Label>
                  <div className={`mt-1 flex h-8 items-center rounded-md border border-border bg-background px-3 text-sm font-semibold ${adjustNum > 0 ? 'text-destructive-text' : 'text-success-text'}`}>
                    {previewEffective > 0 ? formatCurrency(previewEffective) : '—'}
                  </div>
                </div>
                {adjustNum !== 0 && basePriceNum > 0 && (
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground">{adjustNum < 0 ? 'Customer saves' : 'Extra charged'}</Label>
                    <div className="mt-1 flex h-8 items-center rounded-md border border-border bg-background px-3 text-sm font-medium text-muted-foreground">
                      {formatCurrency(Math.abs(basePriceNum - previewEffective))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tier-desc">Description (optional)</Label>
              <Input id="tier-desc" {...register('description')} placeholder="e.g. Recurring customers with good credit" />
            </div>
      </FormDialog>

      {/* Manage Per-Product Overrides Dialog */}
      <Dialog
        visible={!!manageTierId}
        onHide={() => setManageTierId(null)}
        header={
          <div>
            <div className="font-semibold">{managingTier?.name} — Price Overrides</div>
            <div className="text-xs font-normal text-muted-foreground mt-0.5">
              Override the tier discount for specific products only. Most products don't need overrides.
            </div>
          </div>
        }
        style={{ width: '52rem' }}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4 py-2">
          {/* Tier summary */}
          {managingTier && (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-success/10">
                <Percent className="h-5 w-5 text-success-text" />
              </div>
              <div>
                <p className="text-sm font-semibold">
                  {Number(managingTier.adjustmentPercent) > 0
                    ? `+${Number(managingTier.adjustmentPercent)}% markup applies to all products`
                    : Number(managingTier.adjustmentPercent) < 0
                    ? `${Math.abs(Number(managingTier.adjustmentPercent))}% discount applies to all products`
                    : 'No price adjustment — base price used'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {managingTier._count?.customers ?? 0} customers · {managingTier._count?.productPrices ?? 0} override{managingTier._count?.productPrices !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          )}

          {/* Collapsible add override section */}
          {canWrite && (
            <div className="rounded-lg border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => setShowOverrides((v) => !v)}
                className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold hover:bg-muted/50 transition-colors"
              >
                <span>Add Per-Product Override (Advanced)</span>
                {showOverrides ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              {showOverrides && (
                <div className="border-t px-4 py-3 space-y-3 bg-muted/20">
                  <p className="text-xs text-muted-foreground">
                    Use this when a specific product needs a different adjustment % than the tier default. The per-product % takes priority.
                  </p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <Input
                        placeholder="Search product (min 2 chars)..."
                        value={productSearch}
                        onChange={(e) => { setProductSearch(e.target.value); setSelectedProduct(null); }}
                        className="ps-8"
                      />
                    </div>
                    <div className="relative w-44">
                      <Input
                        type="number"
                        min="-100"
                        max="500"
                        step="0.5"
                        placeholder="Override %"
                        value={productAdjPct}
                        onChange={(e) => setProductAdjPct(e.target.value)}
                        className="pe-8"
                      />
                      <Percent className="absolute end-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                    </div>
                    <Button
                      onClick={handleAddOverride}
                      disabled={!selectedProduct || !productAdjPct || setProductPriceMutation.isPending}
                    >
                      {setProductPriceMutation.isPending ? 'Saving...' : 'Set Override'}
                    </Button>
                  </div>
                  {productSearch.length >= 2 && searchableProducts.length > 0 && (
                    <div className="rounded-md border divide-y max-h-40 overflow-y-auto">
                      {searchableProducts.map((p: PriceTierSearchProduct) => (
                        <button
                          key={p.id}
                          onClick={() => { setSelectedProduct(p); setProductSearch(p.name); }}
                          className={`w-full text-start px-3 py-2 text-sm hover:bg-muted/50 transition-colors flex items-center justify-between ${selectedProduct?.id === p.id ? 'bg-primary/10' : ''}`}
                        >
                          <span>{p.name}</span>
                          <span className="text-xs text-muted-foreground">Base: {formatCurrency(Number(p.salePrice))}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedProduct && (
                    <div className="flex items-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-sm">
                      <Package className="h-4 w-4 text-primary" />
                      <span className="font-medium">{selectedProduct.name}</span>
                      <span className="text-muted-foreground">— Base: {formatCurrency(Number(selectedProduct.salePrice))}</span>
                      {productAdjPct && !isNaN(parseFloat(productAdjPct)) && (
                        <span className={parseFloat(productAdjPct) < 0 ? 'text-success-text' : 'text-destructive-text'}>
                          → {formatCurrency(Number(selectedProduct.salePrice) * (1 + parseFloat(productAdjPct) / 100))}
                        </span>
                      )}
                      <button onClick={() => { setSelectedProduct(null); setProductSearch(''); setProductAdjPct(''); }} className="ms-auto text-muted-foreground hover:text-foreground">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Existing overrides */}
          <div>
            <p className="text-sm font-semibold mb-2">
              Product Overrides{tierDetail?.productPrices?.length ? ` (${tierDetail.productPrices.length})` : ''}
            </p>
            {tierDetailLoading ? (
              <SkeletonTable rows={3} cols={4} />
            ) : !tierDetail?.productPrices?.length ? (
              <div className="rounded-lg border py-8 text-center text-sm text-muted-foreground">
                No per-product overrides set. The tier discount % applies to all products automatically.
              </div>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <Table mobileCards>
                  <THead>
                    <Tr hover={false}>
                      <Th>{t('productColumn')}</Th>
                      <Th>{t('tbl.basePrice')}</Th>
                      <Th>{t('tbl.override')}</Th>
                      <Th>{t('tbl.effectivePrice')}</Th>
                      {canWrite && <Th className="text-end">{t('actionsColumn')}</Th>}
                    </Tr>
                  </THead>
                  <TBody>
                    {tierDetail.productPrices.map((pp: PriceTierProductPrice) => {
                      const base = Number(pp.product?.salePrice ?? 0);
                      const overridePct = Number(pp.adjustmentPercent ?? 0);
                      const effectivePrice = base * (1 + overridePct / 100);
                      return (
                        <Tr key={pp.id}>
                          <Td label={t('productColumn')} className="font-medium text-foreground">{pp.product?.name ?? pp.productId}</Td>
                          <Td label={t('tbl.basePrice')} className="text-muted-foreground">{formatCurrency(base)}</Td>
                          <Td label={t('tbl.override')}>
                            {overridePct > 0 ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive-text">
                                <Percent className="h-3 w-3" />+{overridePct}%
                              </span>
                            ) : overridePct < 0 ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs font-semibold text-success-text">
                                <Percent className="h-3 w-3" />{overridePct}%
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs">0% (no change)</span>
                            )}
                          </Td>
                          <Td label={t('tbl.effectivePrice')} className="font-semibold text-foreground">{formatCurrency(effectivePrice)}</Td>
                          {canWrite && (
                            <Td label={t('actionsColumn')} className="text-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setRemoveProductConfirm({ productId: pp.productId, productName: pp.product?.name ?? pp.productId })}
                                title="Remove override"
                              >
                                <Trash2 className="h-4 w-4 text-destructive-text" />
                              </Button>
                            </Td>
                          )}
                        </Tr>
                      );
                    })}
                  </TBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!deleteConfirmId}
        title="Delete Price Tier"
        message="Are you sure you want to delete this price tier? Customers assigned to it will lose their discount."
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteConfirmId && deleteMutation.mutate(deleteConfirmId)}
        onCancel={() => setDeleteConfirmId(null)}
      />

      <ConfirmDialog
        open={!!removeProductConfirm}
        title="Remove Override"
        message={`Remove the % override for "${removeProductConfirm?.productName}"? The tier's adjustment % will apply instead.`}
        confirmLabel="Remove"
        variant="danger"
        loading={removeProductMutation.isPending}
        onConfirm={() => manageTierId && removeProductConfirm && removeProductMutation.mutate({ tierId: manageTierId, productId: removeProductConfirm.productId })}
        onCancel={() => setRemoveProductConfirm(null)}
      />
    </div>
  );
}
